/**
 * MemoryPage — memory center with real rows and section-based grouping.
 *
 * Sections:
 *   * Candidate — extracted, awaiting approval.
 *   * Project — approved, scope='PROJECT'.
 *   * Founder — approved, scope='USER_GLOBAL'.
 *   * Superseded — retained for audit.
 *   * Archived — dropped from active recall.
 *
 * Reads via supabase-js. All rows appear when they exist; sections stay
 * honestly empty otherwise.
 */
import { useEffect, useMemo, useState } from "react";
import { Brain, Loader2 } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import { supabase } from "@/lib/supabaseClient";

function Row({ m }) {
    return (
        <li data-testid={`memory-row-${m.id}`} className="qoryn-panel rounded-2xl px-5 py-4 space-y-2">
            <p className="font-sans text-[14px] text-qoryn-text leading-relaxed">{m.content}</p>
            <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex rounded-full px-2 py-0.5 font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan bg-qoryn-cyan/10">
                    {m.category}
                </span>
                <ProvenanceBadge sourceType={m.source_type} sourceSessionId={m.source_session_id} />
                <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    {new Date(m.updated_at || m.created_at).toLocaleDateString()}
                </span>
            </div>
        </li>
    );
}

function Section({ title, desc, rows, testid }) {
    return (
        <div data-testid={testid} className="space-y-3">
            <div>
                <h2 className="font-display text-[16px] tracking-tight text-qoryn-text">{title}</h2>
                <p className="font-sans text-[12px] text-qoryn-textDim">{desc}</p>
            </div>
            {rows.length === 0 ? (
                <div className="qoryn-panel rounded-2xl p-5">
                    <p className="font-sans text-[12px] text-qoryn-textMuted italic">Nothing here yet.</p>
                </div>
            ) : (
                <ul className="space-y-2">
                    {rows.map((m) => <Row key={m.id} m={m} />)}
                </ul>
            )}
        </div>
    );
}

export default function MemoryPage() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        supabase
            .from("memories")
            .select("id, project_id, scope, content, category, source_type, source_session_id, approval_state, superseded_by, created_at, updated_at")
            .order("updated_at", { ascending: false })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) setError(error.message);
                else setRows(data ?? []);
            });
        return () => { cancelled = true; };
    }, []);

    const buckets = useMemo(() => {
        const out = {
            candidate: [],
            project: [],
            founder: [],
            superseded: [],
            archived: [],
        };
        if (!rows) return out;
        for (const m of rows) {
            if (m.approval_state === "archived") out.archived.push(m);
            else if (m.superseded_by || m.approval_state === "superseded") out.superseded.push(m);
            else if (m.approval_state === "candidate") out.candidate.push(m);
            else if (m.approval_state === "approved" && m.scope === "USER_GLOBAL") out.founder.push(m);
            else if (m.approval_state === "approved") out.project.push(m);
        }
        return out;
    }, [rows]);

    return (
        <div data-testid="memory-page" className="space-y-6">
            <div className="flex items-baseline gap-4 flex-wrap">
                <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Memory Center</h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    What Qoryn remembers, and what&apos;s waiting on your approval.
                </p>
            </div>

            {error && (
                <EmptyState icon={Brain} title="Couldn't load memories." body={error} />
            )}
            {rows === null && !error && (
                <div className="py-6 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
            )}
            {rows !== null && !error && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Section title="Candidate memories" desc="Extracted from conversations, awaiting approval."   rows={buckets.candidate}   testid="memory-section-candidate" />
                    <Section title="Project memory"     desc="Approved facts scoped to a single project."          rows={buckets.project}     testid="memory-section-project" />
                    <Section title="Founder preferences" desc="Approved facts about you across all projects."     rows={buckets.founder}     testid="memory-section-founder" />
                    <Section title="Superseded"          desc="Replaced by a newer decision; kept for audit."     rows={buckets.superseded}  testid="memory-section-superseded" />
                    <Section title="Archived"            desc="Removed from active recall; kept for audit."       rows={buckets.archived}    testid="memory-section-archived" />
                </div>
            )}
        </div>
    );
}
